import { CommonModule } from '@angular/common'
import { moveItemInArray } from '@angular/cdk/drag-drop'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDividerModule } from '@angular/material/divider'
import { MatMenuModule } from '@angular/material/menu'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatSliderModule } from '@angular/material/slider'
import { MatTabsModule } from '@angular/material/tabs'
import { GbDashboardItemDataAccessModule } from '@galabid/gb-dashboard/item/data-access'
import {
  GbDashboardProjectorDataAccessModule,
  PresenterElementTypeEnum,
  ProjectorsFacade,
  ReorderProjectorSlidesPayload,
} from '@galabid/gb-dashboard/projector/data-access'
import { GbDashboardRaffleDataAccessModule, RafflesFacade } from '@galabid/gb-dashboard/raffle/data-access'
import { GbDashboardSharedDataAccessImagesModule, ImagesFacade } from '@galabid/gb-dashboard/shared/data-access-images'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbBreakpointService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-breakpoint-service/gb-breakpoint.service'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { GbColorService } from '@galabid/shared/utils/services'
import { gbCheckLoaded } from '@galabid/shared/utils/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import * as PresenterWidgetActions from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.actions'
import * as PresenterWidgetsActions from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.actions'
import { PresenterWidgetsFacade } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.facade'
import {
  PresenterToolsEnum,
  ProjectorNotificationData,
  BaseWidget,
  CountdownWidgetModel,
  HonourRollWidgetModel,
  ImageWidgetModel,
  ImageWidgetSourceEnum,
  ItemTableWidgetModel,
  NotificationWidgetModel,
  QrCodeWidgetModel,
  RaffleDrawWidgetModel,
  RaffleWinnersWidgetModel,
  TeamScoringWidgetModel,
  PowerPledgeWidgetModel,
  TextWidgetModel,
  ThermometerWidgetModel,
  TotalRaisedWidgetModel,
  WidgetModel,
  WidgetType,
  VideoWidgetModel,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import * as PresenterContainerActions from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.actions'
import { ProjectorContainersFacade } from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.facade'
import { GbDashboardPresenterDataAccessProjectorContainersModule } from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/gb-dashboard-presenter-data-access-projector-containers.module'
import * as ProjectorActions from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.actions'
import * as RafflesActions from 'libs/gb-dashboard/raffle/data-access/src/lib/+state/raffles.actions'
import * as ImagesActions from 'libs/gb-dashboard/shared/data-access-images/src/lib/+state/images.actions'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import * as SummaryActions from 'libs/gb-dashboard/summary/data-access/src/lib/+state/summary.actions'
import { SummaryFacade } from 'libs/gb-dashboard/summary/data-access/src/lib/+state/summary.facade'
import { GbDashboardSummaryDataAccessModule } from 'libs/gb-dashboard/summary/data-access/src/lib/gb-dashboard-summary-data-access.module'
import { combineLatest, firstValueFrom, of } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators'
import { GbPresenterCanvasComponent } from '../gb-presenter-canvas/gb-presenter-canvas.component'
import { GbPresenterConfigSidebarComponent } from '../gb-presenter-config-sidebar/gb-presenter-config-sidebar.component'
import { GbPresenterCustomCssComponent } from '../gb-presenter-custom-css/gb-presenter-custom-css.component'
import { GbPresenterDialogImageSourceComponent } from '../gb-presenter-dialog-image-source/gb-presenter-dialog-image-source.component'
import { GbPresenterDialogRaffleItemComponent } from '../gb-presenter-dialog-raffle-item/gb-presenter-dialog-raffle-item.component'
import { GbPresenterElementsListComponent } from '../gb-presenter-elements-list/gb-presenter-elements-list.component'
import { GbPresenterElementsSidebarComponent } from '../gb-presenter-elements-sidebar/gb-presenter-elements-sidebar.component'
import { GbPresenterLayersComponent, LayerItem } from '../gb-presenter-layers/gb-presenter-layers.component'
import { GbPresenterPreviewComponent } from '../gb-presenter-preview/gb-presenter-preview.component'
import { GbPresenterSaveStatusComponent } from '../gb-presenter-save-status/gb-presenter-save-status.component'
import { GbPresenterSidebarHeaderComponent } from '../gb-presenter-sidebar-header/gb-presenter-sidebar-header.component'
import { GbPresenterSlidesComponent } from '../gb-presenter-slides/gb-presenter-slides.component'
import { GbPresenterTestThemeComponent } from '../gb-presenter-test-theme/gb-presenter-test-theme.component'
import { GbPresenterToolsCanvasComponent } from '../gb-presenter-tools-canvas/gb-presenter-tools-canvas.component'
import { GbPresenterToolsGeneralComponent } from '../gb-presenter-tools-general/gb-presenter-tools-general.component'
import { GbPresenterToolsComponent } from '../gb-presenter-tools/gb-presenter-tools.component'
import { GbPresenterViewComponent } from '../gb-presenter-view/gb-presenter-view.component'
import {
  defaultRaffleDrawWidget,
  defaultSmartText,
  defaultText,
  getDefaultCountdownWidget,
  getDefaultHonourRollWidget,
  getDefaultImageWidget,
  getDefaultItemTableWidget,
  getDefaultNotificationWidget,
  getDefaultQrCodeWidget,
  getDefaultRaffleWinnersWidget,
  getDefaultThermometerWidget,
  getDefaultTotalRaisedWidget,
  getDefaultTeamScoringWidget,
  getDefaultPowerPledgeWidget,
  getDefaultVideoWidget,
} from './gb-presenter-default-widgets'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { AuctionsEntity } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.models'
import { GbPresenterViewOptionsComponent } from '../gb-presenter-view-options/gb-presenter-view-options.component'
import { PRESENTER_CONTEXT } from '../../../../utils/src/lib/gb-presenter-context/presenter-context.token'
import { PresenterContextService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-context/presenter-context.service'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { PresenterContextEnum } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-context/presenter-context.enum'
import { GbPresenterDialogTemplatesComponent } from '../gb-presenter-dialog-templates/gb-presenter-dialog-templates.component'
import { ProjectorsEntity } from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.models'
import {
  GbPresenterDialogRenameSlideComponent,
  GbPresenterDialogRenameSlideData,
} from '../gb-presenter-dialog-rename-slide/gb-presenter-dialog-rename-slide.component'
import { MatProgressBarModule } from '@angular/material/progress-bar'

@Directive({
  selector: '[gbPresenterElementsSidebarHeader]',
  standalone: true,
})
export class GbPresenterElementsSidebarHeaderSlotDirective {}

@Directive({
  selector: '[gbPresenterBottomToolbar]',
  standalone: true,
})
export class GbPresenterBottomToolbarSlotDirective {}

@Directive({
  selector: '[gbPresenterConfigSidebarExtra]',
  standalone: true,
})
export class GbPresenterConfigSidebarExtraSlotDirective {}

@Directive({
  selector: '[gbPresenterConfigSidebarHeader]',
  standalone: true,
})
export class GbPresenterConfigSidebarHeaderSlotDirective {}

@UntilDestroy()
@Component({
  selector: 'gb-presenter-edit',
  imports: [
    MatTabsModule,
    MatSliderModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    CommonModule,
    GbPresenterElementsSidebarComponent,
    GbPresenterConfigSidebarComponent,
    GbPresenterCanvasComponent,
    GbPresenterPreviewComponent,
    GbPresenterSidebarHeaderComponent,
    GbPresenterElementsListComponent,
    GbPresenterSlidesComponent,
    GbDashboardPresenterDataAccessProjectorContainersModule,
    GbPresenterSaveStatusComponent,
    GbPresenterTestThemeComponent,
    GbPresenterToolsCanvasComponent,
    GbPresenterToolsGeneralComponent,
    GbDashboardProjectorDataAccessModule,
    MatProgressSpinnerModule,
    GbDashboardSharedUiModule,
    GbDashboardSummaryDataAccessModule,
    GbPresenterToolsComponent,
    GbPresenterLayersComponent,
    GbDashboardItemDataAccessModule,
    GbDashboardRaffleDataAccessModule,
    GbPresenterViewComponent,
    GbDashboardSharedDataAccessImagesModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    TranslateModule,
    GbPresenterCustomCssComponent,
    GbPresenterViewOptionsComponent,
    MatProgressBarModule,
  ],
  templateUrl: './gb-presenter-edit.component.html',
  styleUrl: './gb-presenter-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterEditComponent implements OnInit, AfterViewInit {
  @ViewChild(GbPresenterCanvasComponent) canvasComponent!: GbPresenterCanvasComponent
  @ViewChild(GbPresenterConfigSidebarComponent) sidebarComponent!: GbPresenterConfigSidebarComponent
  @ViewChild(GbPresenterToolsComponent) presenterToolsComponent!: GbPresenterToolsComponent
  @ViewChild('presenterContentContainer') presenterContentContainer!: ElementRef<HTMLElement>

  @ContentChild(GbPresenterElementsSidebarHeaderSlotDirective)
  readonly elementsSidebarHeaderSlot?: GbPresenterElementsSidebarHeaderSlotDirective

  @ContentChild(GbPresenterBottomToolbarSlotDirective)
  readonly bottomToolbarSlot?: GbPresenterBottomToolbarSlotDirective

  @ContentChild(GbPresenterConfigSidebarHeaderSlotDirective)
  readonly configSidebarHeaderSlot?: GbPresenterConfigSidebarHeaderSlotDirective

  @ContentChild(GbPresenterConfigSidebarExtraSlotDirective)
  readonly configSidebarExtraSlot?: GbPresenterConfigSidebarExtraSlotDirective

  readonly presenterContext = inject(PRESENTER_CONTEXT)
  readonly isTemplateMode = this.presenterContext.mode === 'template'
  private readonly presenterContextService = inject(PresenterContextService)

  previewMode = false
  displayCustomCss = false

  private customCssHeightPx = 200
  private readonly minCustomCssHeightPx = 120
  private isResizingCustomCss = false
  private customCssResizeStartY = 0
  private customCssResizeStartHeight = 200

  PresenterToolsEnum = PresenterToolsEnum
  WidgetType = WidgetType

  // todo implement below
  templateReady$ = of(true)

  projectorContainerDataIsReady$ = combineLatest([
    this.projectorContainersFacade.projectorContainerDataIsReady$,
    this.templateReady$,
  ]).pipe(
    map(([containerDataReady, templateReady]) => {
      if (this.isTemplateMode) {
        return templateReady
      } else {
        return containerDataReady
      }
    }),
  )

  slides$ = this.projectorsFacade.allProjectors$.pipe(
    gbCheckLoaded(this.projectorContainerDataIsReady$),
    withLatestFrom(this.projectorContainersFacade.selectedProjectorContainer$),
    map(([projectors, selectedProjectorContainer]) => {
      return projectors.filter((projector) => projector.container === selectedProjectorContainer?.uuid)
    }),
  )

  selectedProjectorUuid$ = this.projectorsFacade.selectedProjectorId$
  selectedWidget$ = this.presenterWidgetsFacade.selectedWidget$
  savingWidget$ = this.presenterWidgetsFacade.savingWidget$
  canUndo$ = this.presenterWidgetsFacade.canUndo$
  canRedo$ = this.presenterWidgetsFacade.canRedo$
  widgetLoaded$ = combineLatest([this.selectedWidget$, this.savingWidget$]).pipe(
    map(([selectedWidget, savingWidget]) => !selectedWidget || !savingWidget),
  )

  private copiedWidget:
    | { widgetType: WidgetType.TEXT; widget: Partial<TextWidgetModel> }
    | { widgetType: WidgetType.VIDEO; widget: Partial<VideoWidgetModel> }
    | { widgetType: WidgetType.IMAGE; widget: Partial<ImageWidgetModel> }
    | { widgetType: WidgetType.ITEM_TABLE; widget: Partial<ItemTableWidgetModel> }
    | { widgetType: WidgetType.TOTAL_RAISED; widget: Partial<TotalRaisedWidgetModel> }
    | { widgetType: WidgetType.COUNTDOWN; widget: Partial<CountdownWidgetModel> }
    | { widgetType: WidgetType.HONOUR_ROLL; widget: Partial<HonourRollWidgetModel> }
    | { widgetType: WidgetType.RAFFLE_DRAW; widget: Partial<RaffleDrawWidgetModel> }
    | { widgetType: WidgetType.RAFFLE_WINNERS; widget: Partial<RaffleWinnersWidgetModel> }
    | { widgetType: WidgetType.NOTIFICATION; widget: Partial<NotificationWidgetModel> }
    | { widgetType: WidgetType.TEAM_SCORING; widget: Partial<TeamScoringWidgetModel> }
    | { widgetType: WidgetType.POWER_PLEDGE; widget: Partial<PowerPledgeWidgetModel> }
    | { widgetType: WidgetType.THERMOMETER; widget: Partial<ThermometerWidgetModel> }
    | { widgetType: WidgetType.QR_CODE; widget: Partial<QrCodeWidgetModel> }
    | null = null

  layers$ = combineLatest([
    this.presenterWidgetsFacade.allTextWidgets$,
    this.presenterWidgetsFacade.allImageWidgets$,
    this.presenterWidgetsFacade.allItemTableWidgets$,
    this.presenterWidgetsFacade.allTotalRaisedWidgets$,
    this.presenterWidgetsFacade.allCountdownWidgets$,
    this.presenterWidgetsFacade.allHonourRollWidgets$,
    this.presenterWidgetsFacade.allRaffleDrawWidgets$,
    this.presenterWidgetsFacade.allRaffleWinnersWidgets$,
    this.presenterWidgetsFacade.allNotificationWidgets$,
    this.presenterWidgetsFacade.allTeamScoringWidgets$,
    this.presenterWidgetsFacade.allPowerPledgeWidgets$,
    this.presenterWidgetsFacade.allThermometerWidgets$,
    this.presenterWidgetsFacade.allQrCodeWidgets$,
    this.presenterWidgetsFacade.allVideoWidgets$,
    this.selectedProjectorUuid$,
  ]).pipe(
    map(
      ([
        textWidgets,
        imageWidgets,
        itemTableWidgets,
        totalRaisedWidgets,
        countdownWidgets,
        honourRollWidgets,
        raffleDrawWidgets,
        raffleWinnersWidgets,
        notificationWidgets,
        teamScoringWidgets,
        powerPledgeWidgets,
        thermometerWidgets,
        qrCodeWidgets,
        allVideoWidgets,
        selectedProjectorUuid,
      ]) => {
        const shouldFilterByProjector = !this.isTemplateMode || !this.selectedTemplateUuid

        const filterByProjector = <T extends WidgetModel>(widgets: T[]): T[] => {
          if (!shouldFilterByProjector) {
            return widgets
          }

          if (!selectedProjectorUuid) {
            return []
          }

          return widgets.filter((w) => w.projector === selectedProjectorUuid)
        }

        const projectorWidgets = (widgets: WidgetModel[], widgetType: WidgetType): LayerItem[] => {
          return widgets.map((w) => ({
            uuid: w.uuid,
            widgetType,
            z: w.z || 1,
            ...this.getLayerLabel(widgetType, w),
            widget: w,
          }))
        }

        const all: LayerItem[] = [
          ...projectorWidgets(filterByProjector(textWidgets), WidgetType.TEXT),
          ...projectorWidgets(filterByProjector(imageWidgets), WidgetType.IMAGE),
          ...projectorWidgets(filterByProjector(itemTableWidgets), WidgetType.ITEM_TABLE),
          ...projectorWidgets(filterByProjector(totalRaisedWidgets), WidgetType.TOTAL_RAISED),
          ...projectorWidgets(filterByProjector(countdownWidgets), WidgetType.COUNTDOWN),
          ...projectorWidgets(filterByProjector(honourRollWidgets), WidgetType.HONOUR_ROLL),
          ...projectorWidgets(filterByProjector(raffleDrawWidgets), WidgetType.RAFFLE_DRAW),
          ...projectorWidgets(filterByProjector(raffleWinnersWidgets), WidgetType.RAFFLE_WINNERS),
          ...projectorWidgets(filterByProjector(notificationWidgets), WidgetType.NOTIFICATION),
          ...projectorWidgets(filterByProjector(teamScoringWidgets), WidgetType.TEAM_SCORING),
          ...projectorWidgets(filterByProjector(powerPledgeWidgets), WidgetType.POWER_PLEDGE),
          ...projectorWidgets(filterByProjector(thermometerWidgets), WidgetType.THERMOMETER),
          ...projectorWidgets(filterByProjector(allVideoWidgets), WidgetType.VIDEO),
          ...projectorWidgets(filterByProjector(qrCodeWidgets), WidgetType.QR_CODE),
        ]

        return all.sort((a, b) => (b.z ?? 1) - (a.z ?? 1))
      },
    ),
  )

  loadedStatus$ = combineLatest([
    this.presenterWidgetsFacade.countdownWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.imageWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.textWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.smartTextWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.totalRaisedWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.teamScoringWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.powerPledgeWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.notificationWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.itemTableWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.raffleWinnersWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.honourRollWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.thermometerWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.raffleDrawWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.qrCodeWidgetsEditCreateLoaded$,
    this.presenterWidgetsFacade.videoWidgetsEditCreateLoaded$,
    this.projectorContainersFacade.projectorContainersEditCreateLoaded$,
    this.projectorsFacade.editCreateLoaded$,
  ]).pipe(map((loadedStates) => loadedStates.every((loaded) => loaded)))

  addElementLoaded$ = combineLatest({
    countdown: this.presenterWidgetsFacade.countdownWidgetsCreateLoaded$,
    image: this.presenterWidgetsFacade.imageWidgetsCreateLoaded$,
    text: this.presenterWidgetsFacade.textWidgetsCreateLoaded$,
    smartText: this.presenterWidgetsFacade.smartTextWidgetsCreateLoaded$,
    itemTable: this.presenterWidgetsFacade.itemTableWidgetsCreateLoaded$,
    totalRaised: this.presenterWidgetsFacade.totalRaisedWidgetsCreateLoaded$,
    notification: this.presenterWidgetsFacade.notificationWidgetsCreateLoaded$,
    raffleDraw: this.presenterWidgetsFacade.raffleDrawWidgetsCreateLoaded$,
    raffleWinners: this.presenterWidgetsFacade.raffleWinnersWidgetsCreateLoaded$,
    thermometer: this.presenterWidgetsFacade.thermometerWidgetsCreateLoaded$,
    honourRoll: this.presenterWidgetsFacade.honourRollWidgetsCreateLoaded$,
    qrCode: this.presenterWidgetsFacade.qrCodeWidgetsCreateLoaded$,
    teamScoring: this.presenterWidgetsFacade.teamScoringWidgetsCreateLoaded$,
    powerPledge: this.presenterWidgetsFacade.powerPledgeWidgetsCreateLoaded$,
    video: this.presenterWidgetsFacade.videoWidgetsCreateLoaded$,
  }).pipe(
    map(
      ({
        countdown,
        image,
        text,
        smartText,
        itemTable,
        totalRaised,
        notification,
        raffleDraw,
        raffleWinners,
        thermometer,
        honourRoll,
        qrCode,
        teamScoring,
        powerPledge,
        video,
      }) => ({
        [PresenterElementTypeEnum.COUNTDOWN_TIMER]: countdown,
        [PresenterElementTypeEnum.IMAGE]: image,
        [PresenterElementTypeEnum.TEXT]: text,
        [PresenterElementTypeEnum.SMART_TEXT]: smartText,
        [PresenterElementTypeEnum.ITEM_TABLE]: itemTable,
        [PresenterElementTypeEnum.TOTAL_RAISED]: totalRaised,
        [PresenterElementTypeEnum.NOTIFICATION]: notification,
        [PresenterElementTypeEnum.RAFFLE_DRAW]: raffleDraw,
        [PresenterElementTypeEnum.RAFFLE_WINNERS]: raffleWinners,
        [PresenterElementTypeEnum.THERMOMETER]: thermometer,
        [PresenterElementTypeEnum.HONOUR_ROLL]: honourRoll,
        [PresenterElementTypeEnum.QR_CODE]: qrCode,
        [PresenterElementTypeEnum.TEAM_SCORING]: teamScoring,
        [PresenterElementTypeEnum.POWER_PLEDGE]: powerPledge,
        [PresenterElementTypeEnum.VIDEO]: video,
      }),
    ),
  )

  backgroundColorFormControl = new FormControl('#fff', [Validators.required])

  fontFamilyFormControl = new FormControl('', { nonNullable: true })

  customCssControl = new FormControl('', { nonNullable: true })

  backgroundImages$ = this.projectorsFacade.selectedProjectors$.pipe(
    map((projector) => {
      return projector?.background_image ? [projector.background_image] : []
    }),
  )

  backgroundImageUrl$ = this.projectorsFacade.selectedProjectors$.pipe(
    map((projector) => projector?.background_image?.image || null),
  )

  backgroundImageCover$ = this.projectorsFacade.selectedProjectors$.pipe(
    map((projector) => projector?.background_image_cover ?? true),
  )

  showCanvasTools$ = this.selectedWidget$.pipe(map((selectedWidget) => !selectedWidget))

  get backgroundColor() {
    return this.backgroundColorFormControl.value
  }

  get customCss() {
    return this.customCssControl.value
  }

  useTestDataControl = new FormControl(false)

  private selectedAuction: AuctionsEntity | null = null

  constructor(
    private projectorContainersFacade: ProjectorContainersFacade,
    private projectorsFacade: ProjectorsFacade,
    private presenterWidgetsFacade: PresenterWidgetsFacade,
    private rafflesFacade: RafflesFacade,
    private summaryFacade: SummaryFacade,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbNotificationService: GbNotificationService,
    private translateService: TranslateService,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private imagesFacade: ImagesFacade,
    private gbBreakpointService: GbBreakpointService,
    private gbColorService: GbColorService,
    private colorResolver: GbPresenterColorResolverService,
  ) {}

  private async getCurrentLayersFrontToBack(): Promise<Array<{ uuid: string; widgetType: WidgetType; z: number }>> {
    const selectedProjectorId = await firstValueFrom(this.projectorsFacade.selectedProjectorId$)
    if (!selectedProjectorId) {
      return []
    }

    const [
      textWidgets,
      imageWidgets,
      itemTableWidgets,
      totalRaisedWidgets,
      countdownWidgets,
      honourRollWidgets,
      raffleDrawWidgets,
      raffleWinnersWidgets,
      notificationWidgets,
      teamScoringWidgets,
      thermometerWidgets,
      qrCodeWidgets,
    ] = await firstValueFrom(
      combineLatest([
        this.presenterWidgetsFacade.allTextWidgets$,
        this.presenterWidgetsFacade.allImageWidgets$,
        this.presenterWidgetsFacade.allItemTableWidgets$,
        this.presenterWidgetsFacade.allTotalRaisedWidgets$,
        this.presenterWidgetsFacade.allCountdownWidgets$,
        this.presenterWidgetsFacade.allHonourRollWidgets$,
        this.presenterWidgetsFacade.allRaffleDrawWidgets$,
        this.presenterWidgetsFacade.allRaffleWinnersWidgets$,
        this.presenterWidgetsFacade.allNotificationWidgets$,
        this.presenterWidgetsFacade.allTeamScoringWidgets$,
        this.presenterWidgetsFacade.allThermometerWidgets$,
        this.presenterWidgetsFacade.allQrCodeWidgets$,
      ]),
    )

    const items: Array<{ uuid: string; widgetType: WidgetType; z: number }> = []

    const add = (widgets: WidgetModel[], widgetType: WidgetType) => {
      widgets
        .filter((w) => w.projector === selectedProjectorId)
        .forEach((w) => items.push({ uuid: w.uuid, widgetType, z: w.z || 1 }))
    }

    add(textWidgets, WidgetType.TEXT)
    add(imageWidgets, WidgetType.IMAGE)
    add(itemTableWidgets, WidgetType.ITEM_TABLE)
    add(totalRaisedWidgets, WidgetType.TOTAL_RAISED)
    add(countdownWidgets, WidgetType.COUNTDOWN)
    add(honourRollWidgets, WidgetType.HONOUR_ROLL)
    add(raffleDrawWidgets, WidgetType.RAFFLE_DRAW)
    add(raffleWinnersWidgets, WidgetType.RAFFLE_WINNERS)
    add(notificationWidgets, WidgetType.NOTIFICATION)
    add(teamScoringWidgets, WidgetType.TEAM_SCORING)
    add(thermometerWidgets, WidgetType.THERMOMETER)
    add(qrCodeWidgets, WidgetType.QR_CODE)

    return items.sort((a, b) => (b.z ?? 1) - (a.z ?? 1))
  }

  private applyLayerOrder(frontToBack: Array<{ uuid: string; widgetType: WidgetType }>): void {
    const count = frontToBack.length
    const updates = frontToBack.map((item, index) => ({
      uuid: item.uuid,
      widgetType: item.widgetType,
      z: count - index,
    }))
    this.presenterWidgetsFacade.dispatch(PresenterWidgetsActions.reorderWidgets({ updates }))
  }

  onSelectLayer(layer: LayerItem): void {
    this.presenterWidgetsFacade.dispatch(
      PresenterWidgetsActions.selectWidget({
        widget: layer.widget,
        widgetType: layer.widgetType,
      }),
    )
  }

  onSelectBackground(): void {
    this.presenterWidgetsFacade.dispatch(PresenterWidgetsActions.clearSelectedWidget())
  }

  onUndo(): void {
    this.presenterWidgetsFacade.dispatch(PresenterWidgetsActions.undo())
  }

  onRedo(): void {
    this.presenterWidgetsFacade.dispatch(PresenterWidgetsActions.redo())
  }

  onCopy(): void {
    combineLatest([this.presenterWidgetsFacade.selectedWidgetType$, this.presenterWidgetsFacade.selectedWidget$])
      .pipe(take(1))
      .subscribe(([widgetType, selectedWidget]) => {
        if (!widgetType || !selectedWidget) {
          this.copiedWidget = null
          return
        }

        const formValues = this.presenterToolsComponent?.toolsFormGroup?.value ?? {}
        const widgetWithOverrides: Partial<WidgetModel> = {
          ...selectedWidget,
          ...formValues,
        }

        switch (widgetType) {
          case WidgetType.TEXT:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<TextWidgetModel>),
            }
            break
          case WidgetType.IMAGE:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<ImageWidgetModel>),
            }
            break
          case WidgetType.ITEM_TABLE:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<ItemTableWidgetModel>),
            }
            break
          case WidgetType.VIDEO:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<VideoWidgetModel>),
            }
            break
          case WidgetType.TOTAL_RAISED:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<TotalRaisedWidgetModel>),
            }
            break
          case WidgetType.COUNTDOWN:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<CountdownWidgetModel>),
            }
            break
          case WidgetType.HONOUR_ROLL:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<HonourRollWidgetModel>),
            }
            break
          case WidgetType.RAFFLE_DRAW:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<RaffleDrawWidgetModel>),
            }
            break
          case WidgetType.RAFFLE_WINNERS:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<RaffleWinnersWidgetModel>),
            }
            break
          case WidgetType.NOTIFICATION:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<NotificationWidgetModel>),
            }
            break
          case WidgetType.TEAM_SCORING:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<TeamScoringWidgetModel>),
            }
            break
          case WidgetType.THERMOMETER:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<ThermometerWidgetModel>),
            }
            break
          case WidgetType.QR_CODE:
            this.copiedWidget = {
              widgetType,
              widget: this.sanitizeCopiedWidget(widgetWithOverrides as Partial<QrCodeWidgetModel>),
            }
            break
          default:
            this.copiedWidget = null
            return
        }

        if (this.copiedWidget) {
          this.gbNotificationService.message('PRESENTER.WIDGET_COPIED_TO_CLIPBOARD', '', undefined, {
            widgetType: this.translateService.instant(
              `PRESENTER.WIDGET_TYPE.${this.widgetTypeToTranslationKey(widgetType)}`,
            ),
          })
        }
      })
  }

  onPaste(): void {
    if (!this.copiedWidget) {
      return
    }

    const { widgetType, widget } = this.copiedWidget

    switch (widgetType) {
      case WidgetType.TEXT:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createTextWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.IMAGE:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createImageWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.ITEM_TABLE:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createItemTableWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.TOTAL_RAISED:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createTotalRaisedWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.COUNTDOWN:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createCountdownWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.HONOUR_ROLL:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createHonourRollWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.RAFFLE_DRAW:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createRaffleDrawWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.RAFFLE_WINNERS:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createRaffleWinnersWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.NOTIFICATION:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createNotificationWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.TEAM_SCORING:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createTeamScoringWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.THERMOMETER:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createThermometerWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      case WidgetType.QR_CODE:
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createQrCodeWidget({ widget: this.applyPasteOffset(widget) }),
        )
        return
      default:
        return
    }
  }

  private sanitizeCopiedWidget<T extends WidgetModel>(widget: Partial<T>): Partial<T> {
    // Ensure we don't accidentally re-use identity/readonly fields on create.
    const copy = this.deepCopy(widget)
    delete (copy as Partial<BaseWidget>).uuid
    delete (copy as Partial<BaseWidget>).created
    delete (copy as Partial<BaseWidget>).projector
    delete (copy as Partial<BaseWidget>).z
    return copy
  }

  private applyPasteOffset<T extends Partial<BaseWidget>>(widget: T): T {
    const copy = this.deepCopy(widget)

    const x = typeof copy.x === 'number' ? copy.x : null
    const y = typeof copy.y === 'number' ? copy.y : null
    const xUnit = typeof copy.x_unit === 'string' ? copy.x_unit : null
    const yUnit = typeof copy.y_unit === 'string' ? copy.y_unit : null

    const offsetForUnit = (unit: string | null): number => {
      if (unit === '%') return 2
      return 2
    }

    if (x !== null) {
      copy.x = x + offsetForUnit(xUnit)
      copy.x_unit = '%'
    }
    if (y !== null) {
      copy.y = y + offsetForUnit(yUnit)
      copy.y_unit = '%'
    }

    return copy
  }

  private deepCopy<T>(value: T): T {
    const structuredCloneFn = (globalThis as unknown as { structuredClone?: <U>(v: U) => U }).structuredClone
    if (typeof structuredCloneFn === 'function') {
      return structuredCloneFn(value)
    }
    return JSON.parse(JSON.stringify(value)) as T
  }

  onReorderLayers(frontToBack: LayerItem[]): void {
    const notificationLayers = frontToBack.filter((l) => l.widgetType === WidgetType.NOTIFICATION)
    const nonNotificationLayers = frontToBack.filter((l) => l.widgetType !== WidgetType.NOTIFICATION)

    this.applyLayerOrder([
      ...notificationLayers.map((l) => ({ uuid: l.uuid, widgetType: l.widgetType })),
      ...nonNotificationLayers.map((l) => ({ uuid: l.uuid, widgetType: l.widgetType })),
    ])
  }

  onAlignLeft(): void {
    this.alignToPosition('x', 'width', 'start')
  }

  onAlignRight(): void {
    this.alignToPosition('x', 'width', 'end')
  }

  onAlignTop(): void {
    this.alignToPosition('y', 'height', 'start')
  }

  onAlignBottom(): void {
    this.alignToPosition('y', 'height', 'end')
  }

  onAlignHorizontalCenter(): void {
    this.alignToPosition('x', 'width', 'center')
  }

  onAlignVerticalCenter(): void {
    this.alignToPosition('y', 'height', 'center')
  }

  private alignToPosition(axis: 'x' | 'y', dimension: 'width' | 'height', position: 'start' | 'center' | 'end'): void {
    if (position === 'start') {
      this.alignWidget(axis, 0)
      return
    }

    const sizePct = this.getSelectedWidgetSizePct(dimension)
    if (sizePct == null) return

    this.alignWidget(axis, position === 'center' ? (100 - sizePct) / 2 : 100 - sizePct)
  }

  private getSelectedWidgetSizePct(dimension: 'width' | 'height'): number | null {
    const form = this.presenterToolsComponent?.toolsFormGroup
    if (!form) return null

    const value = (form.get(dimension) as FormControl<number | null> | null)?.value
    if (value != null) return value

    const uuid = form.get('uuid')?.value as string | undefined
    if (!uuid) return null

    const canvasEl = this.canvasComponent?.getProjectorCanvasSize()
    const canvasDimPx = dimension === 'width' ? canvasEl?.width : canvasEl?.height
    if (!canvasDimPx) return null

    const wrapperEl = document.querySelector<HTMLElement>(`[data-widget-id="${uuid}"]`)
    if (!wrapperEl) return null

    // we need to use getBoundingClientRect as sometimes the widgets don't have
    // width / height values set explicitly

    const rect = wrapperEl.getBoundingClientRect()
    const widgetDimPx = dimension === 'width' ? rect.width : rect.height

    return (widgetDimPx / canvasDimPx) * 100
  }

  private alignWidget(axis: 'x' | 'y', value: number): void {
    const form = this.presenterToolsComponent?.toolsFormGroup
    if (!form) return

    const uuid = form.get('uuid')?.value as string | undefined
    if (!uuid) return

    const control = form.get(axis) as FormControl<number> | null
    if (!control) return

    control.setValue(value, { emitEvent: false })

    this.presenterWidgetsFacade.dispatch(
      this.isTemplateMode
        ? PresenterWidgetActions.updateSelectedWidgetLocal({
            widget: { uuid, [axis]: value },
          })
        : PresenterWidgetActions.updateSelectedWidget({
            widget: { uuid, [axis]: value },
          }),
    )
  }

  async onBringToFront(): Promise<void> {
    const selectedWidget = await firstValueFrom(this.presenterWidgetsFacade.selectedWidget$)
    if (!selectedWidget) return

    const layers = await this.getCurrentLayersFrontToBack()
    const index = layers.findIndex((l) => l.uuid === selectedWidget.uuid)
    if (index < 0) return

    const next = [...layers]
    const [item] = next.splice(index, 1)
    next.unshift(item)
    this.applyLayerOrder(next)
  }

  private getLayerLabel(widgetType: WidgetType, widget: WidgetModel): Pick<LayerItem, 'label' | 'isLabelKey'> {
    if (widgetType === WidgetType.TEXT) {
      const text = (widget as TextWidgetModel).text
      const trimmed = typeof text === 'string' ? text.trim() : ''

      if (trimmed) {
        return {
          label: trimmed,
          isLabelKey: false,
        }
      }
    }

    return {
      label: this.getWidgetTypeLabelKey(widgetType),
      isLabelKey: true,
    }
  }

  private getWidgetTypeLabelKey(widgetType: WidgetType): string {
    switch (widgetType) {
      case WidgetType.TEXT:
        return 'PRESENTER.ELEMENTS.TEXT'
      case WidgetType.IMAGE:
        return 'PRESENTER.ELEMENTS.IMAGE'
      case WidgetType.ITEM_TABLE:
        return 'PRESENTER.ELEMENTS.ITEM_TABLE'
      case WidgetType.TOTAL_RAISED:
        return 'PRESENTER.ELEMENTS.TOTAL_RAISED'
      case WidgetType.NOTIFICATION:
        return 'PRESENTER.ELEMENTS.NOTIFICATION'
      case WidgetType.RAFFLE_DRAW:
        return 'PRESENTER.ELEMENTS.RAFFLE_DRAW'
      case WidgetType.RAFFLE_WINNERS:
        return 'PRESENTER.ELEMENTS.RAFFLE_WINNERS'
      case WidgetType.THERMOMETER:
        return 'PRESENTER.ELEMENTS.THERMOMETER'
      case WidgetType.COUNTDOWN:
        return 'PRESENTER.ELEMENTS.COUNTDOWN_TIMER'
      case WidgetType.HONOUR_ROLL:
        return 'PRESENTER.ELEMENTS.HONOUR_ROLL'
      case WidgetType.TEAM_SCORING:
        return 'PRESENTER.ELEMENTS.TEAM_SCORING'
      case WidgetType.QR_CODE:
        return 'PRESENTER.ELEMENTS.QR_CODE'
      case WidgetType.VIDEO:
        return 'PRESENTER.ELEMENTS.VIDEO'
      default:
        return 'PRESENTER.ELEMENTS.TEXT'
    }
  }

  async onBringForward(): Promise<void> {
    const selectedWidget = await firstValueFrom(this.presenterWidgetsFacade.selectedWidget$)
    if (!selectedWidget) return

    const layers = await this.getCurrentLayersFrontToBack()
    const index = layers.findIndex((l) => l.uuid === selectedWidget.uuid)
    if (index <= 0) return

    const next = [...layers]
    ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
    this.applyLayerOrder(next)
  }

  async onSendBackward(): Promise<void> {
    const selectedWidget = await firstValueFrom(this.presenterWidgetsFacade.selectedWidget$)
    if (!selectedWidget) return

    const layers = await this.getCurrentLayersFrontToBack()
    const index = layers.findIndex((l) => l.uuid === selectedWidget.uuid)
    if (index < 0 || index >= layers.length - 1) return

    const next = [...layers]
    ;[next[index + 1], next[index]] = [next[index], next[index + 1]]
    this.applyLayerOrder(next)
  }

  async onSendToBack(): Promise<void> {
    const selectedWidget = await firstValueFrom(this.presenterWidgetsFacade.selectedWidget$)
    if (!selectedWidget) return

    const layers = await this.getCurrentLayersFrontToBack()
    const index = layers.findIndex((l) => l.uuid === selectedWidget.uuid)
    if (index < 0) return

    const next = [...layers]
    const [item] = next.splice(index, 1)
    next.push(item)
    this.applyLayerOrder(next)
  }

  ngOnInit(): void {
    this.presenterContextService.setMode(
      this.isTemplateMode ? PresenterContextEnum.TEMPLATE : PresenterContextEnum.AUCTION,
    )
    this.summaryFacade.dispatch(SummaryActions.loadSummary())
    this.rafflesFacade.dispatch(RafflesActions.loadRaffles({}))
    this.trackSelectedSlide()
    this.trackCustomCss()
    this.trackSelectedAuction()
  }

  ngAfterViewInit(): void {
    this.applyCustomCssHeightVar(this.displayCustomCss ? this.customCssHeightPx : 0)

    this.gbBreakpointService.isMobileOrTabletDevice$
      .pipe(
        take(1),
        filter((isMobileOrTabletDevice) => !isMobileOrTabletDevice),
        switchMap(() =>
          this.projectorContainerDataIsReady$.pipe(
            filter((ready) => !!ready),
            take(1),
          ),
        ),
      )
      .subscribe(() => {
        setTimeout(() => this.zoomToFit(), 1)
      })
  }

  toggleCustomCss() {
    this.displayCustomCss = !this.displayCustomCss

    if (this.displayCustomCss) {
      this.applyCustomCssHeightVar()
    } else {
      this.applyCustomCssHeightVar(0)
    }
  }

  get canvasMaxHeight(): string {
    if (this.isTemplateMode) {
      return `100%`
    }
    return `calc(100% - 100px - var(--gb-presenter-custom-css-height, 0px))`
  }

  get selectedTemplateUuid(): string | undefined {
    return this.isTemplateMode ? this.presenterContextService.templateUuid : undefined
  }

  onCustomCssResizeMouseDown(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()

    this.isResizingCustomCss = true
    this.customCssResizeStartY = event.clientY
    this.customCssResizeStartHeight = this.customCssHeightPx
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }

  @HostListener('window:mousemove', ['$event'])
  onWindowMouseMove(event: MouseEvent): void {
    if (!this.isResizingCustomCss || !this.displayCustomCss) {
      return
    }

    const deltaY = event.clientY - this.customCssResizeStartY
    const nextHeight = this.customCssResizeStartHeight - deltaY
    const maxHeight = this.getMaxCustomCssHeightPx()

    this.customCssHeightPx = Math.max(this.minCustomCssHeightPx, Math.min(maxHeight, nextHeight))
    this.applyCustomCssHeightVar()
  }

  @HostListener('window:mouseup')
  onWindowMouseUp(): void {
    if (!this.isResizingCustomCss) {
      return
    }

    this.isResizingCustomCss = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  private applyCustomCssHeightVar(forcedHeightPx?: number): void {
    const hostEl = this.presenterContentContainer?.nativeElement
    if (!hostEl) {
      return
    }

    const heightPx = forcedHeightPx ?? this.customCssHeightPx
    hostEl.style.setProperty('--gb-presenter-custom-css-height', `${heightPx}px`)
  }

  private getMaxCustomCssHeightPx(): number {
    const hostEl = this.presenterContentContainer?.nativeElement
    const hostHeight = hostEl?.clientHeight ?? 0

    // Keep some room for the bottom toolbar + canvas; cap at 80% of available height.
    const byPercent = Math.floor(hostHeight * 0.8)
    const byReserve = Math.max(0, hostHeight - 200)
    return Math.max(this.minCustomCssHeightPx, Math.min(byPercent, byReserve || byPercent))
  }

  activatePreview() {
    this.previewMode = true
  }

  disablePreview() {
    this.previewMode = false
  }

  getWidget<T extends { uuid: string }>(
    widget: T,
    selectedWidget: WidgetModel | null,
    savingWidget: Partial<WidgetModel> | null | undefined,
  ) {
    if (widget.uuid === selectedWidget?.uuid) {
      return { ...widget, ...this.presenterToolsComponent.toolsFormGroup.value }
    }

    if (widget.uuid === savingWidget?.uuid) {
      return { ...widget, ...savingWidget } as T
    }

    return widget
  }

  getSelectedWidgetOverrides(): Partial<WidgetModel> | null {
    if (!this.presenterToolsComponent?.toolsFormGroup) {
      return null
    }

    return this.presenterToolsComponent.toolsFormGroup.value
  }

  onCanvasWidgetSelected(event: { widget: WidgetModel; widgetType: WidgetType }): void {
    this.presenterWidgetsFacade.selectedWidget$.pipe(take(1)).subscribe((selectedWidget) => {
      this.selectWidget(event.widget, event.widgetType, selectedWidget)
    })
  }

  onUpdateProjectorContainerName(newName: string): void {
    this.projectorContainersFacade.dispatch(
      PresenterContainerActions.updateSelectedProjectorContainer({
        projectorContainer: { name: newName },
      }),
    )
  }

  /** Widgets */

  clearSelectedWidget() {
    this.presenterWidgetsFacade.selectedWidget$.pipe(take(1)).subscribe((selectedWidget) => {
      if (selectedWidget) {
        if (
          'raffle_item' in selectedWidget &&
          this.presenterToolsComponent.toolsFormGroup.get('raffle_item')?.value === null &&
          !this.isTemplateMode
        ) {
          this.openRaffleItemWarning(selectedWidget)
          return
        }

        this.saveSelectedWidgetFormValues(selectedWidget)
      }
      this.presenterWidgetsFacade.dispatch(PresenterWidgetsActions.clearSelectedWidget())
    })
  }

  onPreviewWidget(widgetType: WidgetType): void {
    this.presenterWidgetsFacade.selectedWidget$.pipe(take(1)).subscribe((selectedWidget) => {
      if (!selectedWidget) {
        return
      }

      switch (widgetType) {
        case WidgetType.RAFFLE_DRAW:
          this.canvasComponent?.triggerRaffleDrawPreview(selectedWidget.uuid)
          return
        case WidgetType.RAFFLE_WINNERS:
          this.canvasComponent?.triggerRaffleDrawPreview(selectedWidget.uuid)
          return
        default:
          return
      }
    })
  }

  onResetPreviewWidget(widgetType: WidgetType): void {
    this.presenterWidgetsFacade.selectedWidget$.pipe(take(1)).subscribe((selectedWidget) => {
      if (!selectedWidget) {
        return
      }

      switch (widgetType) {
        case WidgetType.RAFFLE_DRAW:
          this.canvasComponent?.resetRaffleDrawPreview(selectedWidget.uuid)
          return
        case WidgetType.RAFFLE_WINNERS:
          this.canvasComponent?.resetRaffleDrawPreview(selectedWidget.uuid)
          return
        default:
          return
      }
    })
  }

  onPreviewNotification(data: ProjectorNotificationData): void {
    this.canvasComponent?.triggerNotification(data)
  }

  onTextWidgetCommitted(event: { uuid: string; text: string }): void {
    if (!this.presenterToolsComponent?.toolsFormGroup) {
      return
    }

    const selectedWidget = this.presenterToolsComponent.toolsFormGroup.get('uuid')?.value
    if (selectedWidget !== event.uuid) {
      return
    }

    const textControl = this.presenterToolsComponent.toolsFormGroup.get('text') as FormControl<string> | null
    if (!textControl) {
      return
    }

    if (textControl.value === event.text) {
      return
    }

    textControl.setValue(event.text)
  }

  onTextWidgetEditFinished(event: { uuid: string }): void {
    const selectedWidgetUuid = this.presenterToolsComponent?.toolsFormGroup?.get('uuid')?.value
    if (selectedWidgetUuid !== event.uuid) {
      return
    }

    this.clearSelectedWidget()
  }

  private saveSelectedWidgetFormValues(selectedWidget: WidgetModel): void {
    if (!this.presenterToolsComponent?.toolsFormGroup) {
      return
    }

    if (this.presenterToolsComponent.toolsFormGroup.invalid) {
      this.presenterToolsComponent.toolsFormGroup.markAllAsTouched()
      return
    }

    const formValues = this.presenterToolsComponent.toolsFormGroup.value
    this.presenterWidgetsFacade.dispatch(
      this.isTemplateMode
        ? PresenterWidgetActions.updateSelectedWidgetLocal({
            widget: { ...formValues, uuid: selectedWidget.uuid },
          })
        : PresenterWidgetActions.updateSelectedWidget({
            widget: { ...formValues, uuid: selectedWidget.uuid },
          }),
    )
  }

  openRaffleItemWarning(selectedWidget: WidgetModel) {
    this.gbDashboardDialogService
      .getDialogObs(GbPresenterDialogRaffleItemComponent, {
        width: '520px',
        data: {
          widgetUuid: selectedWidget.uuid,
        },
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (!this.presenterToolsComponent?.toolsFormGroup) {
          return
        }

        const raffleItemControl = this.presenterToolsComponent.toolsFormGroup.get('raffle_item') as FormControl | null
        if (!raffleItemControl) {
          return
        }

        const raffleItemUuid = typeof result === 'string' ? result : null
        raffleItemControl.setValue(raffleItemUuid)

        const formValues = this.presenterToolsComponent.toolsFormGroup.value
        this.presenterWidgetsFacade.dispatch(
          this.isTemplateMode
            ? PresenterWidgetActions.updateSelectedWidgetLocal({
                widget: { ...formValues, uuid: selectedWidget.uuid, raffle_item: raffleItemUuid },
              })
            : PresenterWidgetActions.updateSelectedWidget({
                widget: { ...formValues, uuid: selectedWidget.uuid, raffle_item: raffleItemUuid },
              }),
        )

        this.presenterWidgetsFacade.dispatch(PresenterWidgetsActions.clearSelectedWidget())
      })
  }

  selectWidget(widget: WidgetModel, type: WidgetType, selectedWidget: WidgetModel | null) {
    if (selectedWidget?.uuid === widget.uuid) {
      return
    }

    if (selectedWidget) {
      this.saveSelectedWidgetFormValues(selectedWidget)
    }

    this.presenterWidgetsFacade.dispatch(PresenterWidgetsActions.selectWidget({ widget, widgetType: type }))
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeyDown(event: KeyboardEvent): void {
    const activeElement = document.activeElement as HTMLElement | null
    const activeTagName = activeElement?.tagName?.toLowerCase()

    const isEditable =
      activeTagName === 'input' || activeTagName === 'textarea' || activeElement?.isContentEditable === true

    const key = event.key?.toLowerCase()
    const hasModifier = event.metaKey || event.ctrlKey

    if (hasModifier && key === 'c') {
      if (isEditable) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      this.onCopy()
      return
    }

    if (hasModifier && key === 'v') {
      if (isEditable) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      this.onPaste()
      return
    }

    if (hasModifier && key === 'z' && !event.altKey) {
      if (isEditable) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      if (event.shiftKey) {
        this.onRedo()
      } else {
        this.onUndo()
      }

      return
    }

    if (event.key !== 'Delete' && event.key !== 'Backspace') {
      return
    }

    if (isEditable) {
      return
    }

    event.preventDefault()
    this.presenterWidgetsFacade.selectedWidget$.pipe(take(1)).subscribe((selectedWidget) => {
      if (!selectedWidget) {
        return
      }
      this.deleteSelectedWidget()
    })
  }

  updateWidgetPosition(widget: Partial<WidgetModel>) {
    if (!widget?.uuid || widget.x === undefined || widget.y === undefined) {
      return
    }

    const round0 = (value: number): number => {
      return Math.round(value)
    }

    const form = this.presenterToolsComponent?.toolsFormGroup
    if (!form) {
      return
    }

    const formWidgetUuid = form.get('uuid')?.value
    if (formWidgetUuid && formWidgetUuid !== widget.uuid) {
      return
    }

    ;(form.get('x') as FormControl<number> | null)?.setValue(widget.x, { emitEvent: false })
    ;(form.get('y') as FormControl<number> | null)?.setValue(widget.y, { emitEvent: false })

    if (widget.width !== undefined) {
      const nextWidth = widget.width === null ? null : round0(widget.width)
      ;(form.get('width') as FormControl<number | null> | null)?.setValue(nextWidth, { emitEvent: false })
    }
    if (widget.height !== undefined) {
      const nextHeight = widget.height === null ? null : round0(widget.height)
      ;(form.get('height') as FormControl<number | null> | null)?.setValue(nextHeight, { emitEvent: false })
    }

    const updatePayload: Partial<WidgetModel> = {
      uuid: widget.uuid,
      x: widget.x,
      y: widget.y,
    }

    if (widget.width !== undefined) {
      updatePayload.width = widget.width === null ? null : round0(widget.width)
    }
    if (widget.height !== undefined) {
      updatePayload.height = widget.height === null ? null : round0(widget.height)
    }

    this.presenterWidgetsFacade.dispatch(
      this.isTemplateMode
        ? PresenterWidgetActions.updateSelectedWidgetLocal({
            widget: updatePayload,
          })
        : PresenterWidgetActions.updateSelectedWidget({
            widget: updatePayload,
          }),
    )
  }

  deleteSelectedWidget() {
    this.presenterWidgetsFacade.dispatch(PresenterWidgetsActions.deleteSelectedWidget())
  }

  addWidget(event: PresenterElementTypeEnum, position?: { x: number; y: number }) {
    let overrides: Partial<BaseWidget> = {}

    const getCenteredOverrides = (
      width: number | null | undefined,
      height: number | null | undefined,
    ): Partial<BaseWidget> => {
      if (!position) return {}
      const w = width ?? 20 // Default fallback width if null
      const h = height ?? 10 // Default fallback height if null
      return {
        x: position.x - w / 2,
        y: position.y - h / 2,
        x_unit: '%',
        y_unit: '%',
      }
    }

    switch (event) {
      case PresenterElementTypeEnum.TEXT:
        overrides = position ? { x: position.x - 10, y: position.y - 5, x_unit: '%', y_unit: '%' } : {}
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createTextWidget({ widget: { ...defaultText, ...overrides } }),
        )
        return
      case PresenterElementTypeEnum.SMART_TEXT:
        overrides = position ? { x: position.x - 10, y: position.y - 5, x_unit: '%', y_unit: '%' } : {}
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createSmartTextWidget({ widget: { ...defaultSmartText, ...overrides } }),
        )
        return
      case PresenterElementTypeEnum.IMAGE:
        this.gbDashboardDialogService
          .getDialogObs(GbPresenterDialogImageSourceComponent, {
            width: '700px',
          })
          .pipe(take(1))
          .subscribe((result) => {
            const imageSource = result as ImageWidgetSourceEnum | null
            if (!imageSource) {
              return
            }

            const imageOverrides = getCenteredOverrides(getDefaultImageWidget().width, 20)

            if (imageSource === ImageWidgetSourceEnum.Custom) {
              this.imagesFacade.dispatch(
                ImagesActions.openTemplateImageUploadDialog({
                  templateUuid:
                    this.isTemplateMode && this.selectedTemplateUuid ? this.selectedTemplateUuid : undefined,
                  onSuccess: (images) => {
                    if (!images?.length) return

                    return PresenterWidgetsActions.createImageWidget({
                      widget: {
                        ...getDefaultImageWidget(),
                        ...imageOverrides,
                        image_source: ImageWidgetSourceEnum.Custom,
                        image: images[0].image,
                      },
                    })
                  },
                }),
              )

              return
            }

            this.presenterWidgetsFacade.dispatch(
              PresenterWidgetsActions.createImageWidget({
                widget: {
                  ...getDefaultImageWidget(),
                  ...imageOverrides,
                  image_source: imageSource,
                  image: null,
                },
              }),
            )
          })
        return
      case PresenterElementTypeEnum.ITEM_TABLE:
        overrides = getCenteredOverrides(getDefaultItemTableWidget().width, getDefaultItemTableWidget().height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createItemTableWidget({
            widget: { ...getDefaultItemTableWidget(), ...overrides },
          }),
        )
        return
      case PresenterElementTypeEnum.TOTAL_RAISED:
        overrides = position ? { x: position.x - 15, y: position.y - 5, x_unit: '%', y_unit: '%' } : {}
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createTotalRaisedWidget({
            widget: {
              ...getDefaultTotalRaisedWidget(
                this.backgroundColor
                  ? this.gbColorService.getContrast(this.colorResolver.resolve(this.backgroundColor))
                  : '#000000',
              ),
              ...overrides,
            },
          }),
        )
        return
      case PresenterElementTypeEnum.NOTIFICATION:
        overrides = getCenteredOverrides(getDefaultNotificationWidget().width, getDefaultNotificationWidget().height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createNotificationWidget({
            widget: { ...getDefaultNotificationWidget(), ...overrides },
          }),
        )
        return
      case PresenterElementTypeEnum.RAFFLE_DRAW:
        overrides = getCenteredOverrides(defaultRaffleDrawWidget.width, defaultRaffleDrawWidget.height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createRaffleDrawWidget({ widget: { ...defaultRaffleDrawWidget, ...overrides } }),
        )
        return
      case PresenterElementTypeEnum.RAFFLE_WINNERS:
        overrides = getCenteredOverrides(getDefaultRaffleWinnersWidget().width, getDefaultRaffleWinnersWidget().height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createRaffleWinnersWidget({
            widget: {
              ...getDefaultRaffleWinnersWidget(
                this.backgroundColor
                  ? this.gbColorService.getContrast(this.colorResolver.resolve(this.backgroundColor))
                  : '#000000',
              ),
              ...overrides,
            },
          }),
        )
        return
      case PresenterElementTypeEnum.VIDEO:
        overrides = getCenteredOverrides(getDefaultVideoWidget().width, getDefaultVideoWidget().height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createVideoWidget({
            widget: { ...getDefaultVideoWidget(), ...overrides },
          }),
        )
        return
      case PresenterElementTypeEnum.THERMOMETER:
        overrides = getCenteredOverrides(getDefaultThermometerWidget().width, getDefaultThermometerWidget().height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createThermometerWidget({
            widget: { ...getDefaultThermometerWidget(), ...overrides },
          }),
        )
        return
      case PresenterElementTypeEnum.COUNTDOWN_TIMER: {
        const defaultCountdownEnd = new Date(Date.now() + 2 * 60 * 60 * 1000) // Using 2 hours, same as the old presenter
        const hasAuctionCloseTime = !!(this.selectedAuction?.scheduled_end || this.selectedAuction?.closing_time)
        overrides = position ? { x: position.x - 15, y: position.y - 10, x_unit: '%', y_unit: '%' } : {}
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createCountdownWidget({
            widget: {
              ...getDefaultCountdownWidget(),
              ...overrides,
              end: defaultCountdownEnd.toISOString(),
              end_on_auction_schedule_close: hasAuctionCloseTime,
            },
          }),
        )
        return
      }
      case PresenterElementTypeEnum.HONOUR_ROLL:
        overrides = getCenteredOverrides(getDefaultHonourRollWidget().width, getDefaultHonourRollWidget().height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createHonourRollWidget({
            widget: { ...getDefaultHonourRollWidget(), ...overrides },
          }),
        )
        return
      case PresenterElementTypeEnum.QR_CODE:
        overrides = getCenteredOverrides(getDefaultQrCodeWidget().width, getDefaultQrCodeWidget().height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createQrCodeWidget({
            widget: { ...getDefaultQrCodeWidget(), ...overrides },
          }),
        )
        return
      case PresenterElementTypeEnum.TEAM_SCORING:
        overrides = getCenteredOverrides(getDefaultTeamScoringWidget().width, getDefaultTeamScoringWidget().height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createTeamScoringWidget({
            widget: { ...getDefaultTeamScoringWidget(), ...overrides },
          }),
        )
        return
      case PresenterElementTypeEnum.POWER_PLEDGE:
        overrides = getCenteredOverrides(getDefaultPowerPledgeWidget().width, getDefaultPowerPledgeWidget().height)
        this.presenterWidgetsFacade.dispatch(
          PresenterWidgetsActions.createPowerPledgeWidget({
            widget: { ...getDefaultPowerPledgeWidget(), ...overrides },
          }),
        )
        return
      case PresenterElementTypeEnum.TEMPLATES:
      case PresenterElementTypeEnum.CUSTOM_CSS:
      case PresenterElementTypeEnum.DIVIDER:
      case PresenterElementTypeEnum.ADVANCED_OPTIONS_DIVIDER:
      default:
        return
    }
  }

  onElementDropped(event: { type: PresenterElementTypeEnum; x: number; y: number }): void {
    this.addWidget(event.type, { x: event.x, y: event.y })
  }

  openTemplatesDialog() {
    this.gbDashboardDialogService.openDialog(GbPresenterDialogTemplatesComponent, {
      width: '800px',
    })
  }

  /** View */

  zoomLevel = signal(1)

  private readonly minZoom = 0.25
  private readonly maxZoom = 5

  setZoom(zoom: number): void {
    const safeZoom = Number.isFinite(zoom) ? zoom : 1
    const clamped = Math.max(this.minZoom, Math.min(this.maxZoom, safeZoom))
    this.zoomLevel.set(clamped)
  }

  zoomToFit(): void {
    const outer = this.canvasComponent?.getOuterContainerSize()
    const currentCanvas = this.canvasComponent?.getProjectorCanvasSize()

    if (!outer || !currentCanvas || !currentCanvas.width || !currentCanvas.height) {
      return
    }

    const paddingFactor = 0.95

    const scale = Math.min(
      (outer.width * paddingFactor) / currentCanvas.width,
      (outer.height * paddingFactor) / currentCanvas.height,
    )

    if (!Number.isFinite(scale) || scale <= 0) {
      return
    }

    this.setZoom(this.zoomLevel() * scale)
  }

  zoomToFill(): void {
    const outer = this.canvasComponent?.getOuterContainerSize()
    const currentCanvas = this.canvasComponent?.getProjectorCanvasSize()

    if (!outer || !currentCanvas || !currentCanvas.width || !currentCanvas.height) {
      return
    }

    const scale = Math.max(outer.width / currentCanvas.width, outer.height / currentCanvas.height)

    if (!Number.isFinite(scale) || scale <= 0) {
      return
    }

    this.setZoom(this.zoomLevel() * scale)
  }

  /** Slides */

  trackSelectedSlide() {
    this.projectorsFacade.selectedProjectors$.pipe(untilDestroyed(this)).subscribe((projector) => {
      this.backgroundColorFormControl.setValue(projector?.background_color || '#fff')
      this.fontFamilyFormControl.setValue(projector?.font_family || '', { emitEvent: false })
    })
  }

  private trackCustomCss(): void {
    this.projectorsFacade.selectedProjectors$.pipe(untilDestroyed(this)).subscribe((projector) => {
      this.customCssControl.setValue(projector?.custom_css || '', { emitEvent: false })
    })

    this.customCssControl.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        withLatestFrom(this.projectorsFacade.selectedProjectors$),
        untilDestroyed(this),
      )
      .subscribe(([customCss, projector]) => {
        if (!projector) {
          return
        }

        if ((projector.custom_css || '') === (customCss || '')) {
          return
        }

        this.projectorsFacade.dispatch(
          this.isTemplateMode
            ? ProjectorActions.patchSelectedProjectorLocal({ projector: { custom_css: customCss } })
            : ProjectorActions.updateSelectedProjector({ projector: { custom_css: customCss } }),
        )
      })
  }

  private trackSelectedAuction(): void {
    this.selectedAuctionFacade.auction$.pipe(untilDestroyed(this)).subscribe((auction) => {
      this.selectedAuction = auction
    })
  }

  openBackgroundImageUploadDialog(): void {
    this.imagesFacade.dispatch(
      ImagesActions.openTemplateImageUploadDialog({
        templateUuid: this.isTemplateMode && this.selectedTemplateUuid ? this.selectedTemplateUuid : undefined,
        onSuccess: (images) => {
          const image = images?.[0]?.image || null
          return this.isTemplateMode
            ? ProjectorActions.patchSelectedProjectorLocal({
                projector: { background_image: image, background_image_file: null },
              })
            : ProjectorActions.updateSelectedProjector({ projector: { background_image: image } })
        },
      }),
    )
  }

  clearSlideBackgroundImage(): void {
    this.projectorsFacade.dispatch(
      this.isTemplateMode
        ? ProjectorActions.patchSelectedProjectorLocal({
            projector: { background_image: null, background_image_file: null },
          })
        : ProjectorActions.updateSelectedProjector({ projector: { background_image: null } }),
    )
  }

  updateSlideBackgroundImageCover(cover: boolean): void {
    this.projectorsFacade.dispatch(
      this.isTemplateMode
        ? ProjectorActions.patchSelectedProjectorLocal({ projector: { background_image_cover: cover } })
        : ProjectorActions.updateSelectedProjector({ projector: { background_image_cover: cover } }),
    )
  }

  onSlideSelected(slideId: string): void {
    this.selectSlide(slideId)
  }

  selectSlide(uuid: string) {
    this.presenterWidgetsFacade.dispatch(PresenterWidgetsActions.clearSelectedWidget())

    this.projectorsFacade.dispatch(
      ProjectorActions.selectProjector({
        uuid,
      }),
    )
  }

  onSlideAdded(): void {
    this.projectorsFacade.dispatch(ProjectorActions.openAddSlideDialog())
  }

  onSlideDeleted(uuid: string): void {
    this.projectorContainersFacade.selectedProjectorContainer$
      .pipe(take(1), untilDestroyed(this))
      .subscribe((container) => {
        if (container?.uuid) {
          this.projectorsFacade.dispatch(ProjectorActions.deleteSlide({ uuid, containerUuid: container.uuid }))
        }
      })
  }

  onSlideDuplicated(slideId: string): void {
    this.projectorsFacade.dispatch(ProjectorActions.openDuplicateSlideDialog({ projectorUuid: slideId }))
  }

  private buildSlidesToUpdate(
    newSlideUuids: string[],
    currentSlides: ProjectorsEntity[],
  ): ReorderProjectorSlidesPayload[] {
    const updatedSlides: ReorderProjectorSlidesPayload[] = []

    newSlideUuids.forEach((newUuid, newIndex) => {
      const currentSlide = currentSlides.find((slide) => slide.uuid === newUuid)
      const newOrder = newIndex + 1

      if (currentSlide && currentSlide.order !== newOrder) {
        updatedSlides.push({ uuid: currentSlide.uuid, order: newOrder })
      }
    })

    return updatedSlides
  }

  onSlideRenamed(slideUuid: string): void {
    this.slides$
      .pipe(
        take(1),
        switchMap((slides) => {
          const slide = slides.find((s) => s.uuid === slideUuid)

          return this.gbDashboardDialogService
            .getDialogObs(GbPresenterDialogRenameSlideComponent, {
              disableClose: true,
              minWidth: 450,
              maxWidth: 600,
              data: {
                name: slide?.name || '',
              } satisfies GbPresenterDialogRenameSlideData,
            })
            .pipe(map((result) => ({ slideUuid, result })))
        }),
        filter(({ result }) => typeof result === 'string'),
        map(({ slideUuid, result }) => ({ slideUuid, name: (result as string).trim() })),
        filter(({ name }) => !!name),
        untilDestroyed(this),
      )
      .subscribe(({ slideUuid, name }) => {
        this.projectorsFacade.dispatch(ProjectorActions.updateProjector({ projector: { uuid: slideUuid, name } }))
      })
  }

  onSlideReordered(event: { fromIndex: number; toIndex: number }): void {
    if (event.fromIndex === event.toIndex) {
      return
    }

    this.slides$.pipe(take(1), untilDestroyed(this)).subscribe((slides) => {
      const reorderedSlideUuids = slides.map((slide) => slide.uuid)
      moveItemInArray(reorderedSlideUuids, event.fromIndex, event.toIndex)

      const slidesToUpdate = this.buildSlidesToUpdate(reorderedSlideUuids, slides)

      if (slidesToUpdate.length > 0) {
        this.projectorsFacade.dispatch(ProjectorActions.reorderSlides({ slides: slidesToUpdate }))
      }
    })
  }

  widgetTypeToTranslationKey(widgetType: WidgetType): string {
    return widgetType.replace(/-/g, '_').toUpperCase()
  }

  updateSlideColor(color: string): void {
    this.projectorsFacade.dispatch(
      this.isTemplateMode
        ? ProjectorActions.patchSelectedProjectorLocal({ projector: { background_color: color } })
        : ProjectorActions.updateSelectedProjector({ projector: { background_color: color } }),
    )
  }

  updateSlideFontOverride(field: 'color' | 'font_family', value: string, _formControl: FormControl): void {
    this.projectorsFacade.dispatch(
      this.isTemplateMode
        ? ProjectorActions.patchSelectedProjectorLocal({ projector: { [field]: value } })
        : ProjectorActions.updateSelectedProjector({ projector: { [field]: value } }),
    )
  }
}
